<template>
  <data-orchestration-history-action v-if="log.action" :log="log" />
  <div
    v-else-if="templateStepOutcomes.length"
    :id="`log-${log.started_at}`"
    class="relative space-y-3 rounded-sm p-2"
    :class="{
      'bg-gray-100/70': isLastBlockRan,
    }"
  >
    <div class="flex items-center space-x-4">
      <span data-cy="history-block-name" class="font-semibold text-gray-500">
        {{ blockName }}
      </span>

      <template v-if="isLastBlockRan">
        <div class="line-separator"></div>
        <span class="text-xs font-semibold text-gray-300 uppercase">
          {{ $t("ORCHESTRATION.LAST_BLOCK_RAN") }}
        </span>
        <div class="line-separator"></div>
      </template>
      <div v-else class="line-separator"></div>

      <span class="block-outcome font-semibold uppercase">
        {{
          $t(
            `ORCHESTRATION.${
              log.outcome?.toUpperCase() || "OUTCOME_NOT_AVAILABLE"
            }`
          )
        }}
      </span>
    </div>
    <div class="space-y-3">
      <div
        v-for="outcome in templateStepOutcomes"
        :key="`log-${log.step_id}-${outcome.outcome}`"
        class="relative"
      >
        <div v-if="outcome.checks?.length" class="absolute flex items-center">
          <icon-base
            class="-translate-y-0-25 transform"
            :icon="IconStatusArrow"
            width="8"
            height="6"
          />
          <span
            class="rounded-full border bg-white px-2 text-xs font-semibold uppercase"
          >
            {{ $t(`ORCHESTRATION.${outcome.outcome.toUpperCase()}`) }}
          </span>
        </div>
        <div
          v-for="(check, checkIndex) in outcome.checks"
          :key="`log-${log.step_id}-${outcome.outcome}-${check.comparator}`"
        >
          <data-orchestration-history-sub-row
            v-for="condition in check.conditions"
            :key="`log-${log.step_id}-${outcome.outcome}-${check.comparator}-${condition.attribute}`"
            :condition="condition"
            :log="log"
            :outcome="outcome"
          />
          <div
            v-if="
              !!outcome.checks &&
              outcome.checks.length > 1 &&
              checkIndex !== outcome.checks.length - 1
            "
            class="grid w-full grid-cols-5"
          >
            <span></span>
            <span
              class="my-1 -translate-x-4 transform text-xs font-semibold text-gray-300 uppercase"
            >
              {{ $t("COMMON.OR") }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import compact from "lodash/compact";
import { storeToRefs } from "pinia";

import { COLORS } from "@/helpers/constants";
import useApplicationsStore from "@/stores/applications";

import type { IDataOrchestrationLogEntry } from "@/models/applications";
import type {
  DataOrchestrationOutcome,
  IDataOrchestrationFlowCheckCondition,
  IDataOrchestrationFlowOutcome,
} from "@/models/orchestration";
import type { PropType } from "vue";
</script>

<script setup lang="ts">
import DataOrchestrationHistoryAction from "@/components/deals/dataOrchestration/status/DataOrchestrationHIstoryAction.vue";
import DataOrchestrationHistorySubRow from "@/components/deals/dataOrchestration/status/DataOrchestrationHistorySubRow.vue";
import IconStatusArrow from "@/components/icons/IconStatusArrow.vue";
import IconBase from "@/components/ui/IconBase.vue";

const props = defineProps({
  log: {
    type: Object as PropType<IDataOrchestrationLogEntry>,
    required: true,
  },
  isLastBlockRan: {
    type: Boolean,
    default: false,
  },
  activeTab: {
    type: String,
    required: true,
  },
});

const STATUS_COLOR_MAP: Record<DataOrchestrationOutcome, number> = {
  pass: 0,
  fail: 2,
  undetermined: 15,
  ["manual review"]: 5,
};

const { t } = useI18n();
const applicationsStore = useApplicationsStore();
const { activeDataOrchestrationLog } = storeToRefs(applicationsStore);
const allTemplateSteps = computed(
  () => activeDataOrchestrationLog.value?.template?.content?.steps || []
);

const step = computed(() =>
  allTemplateSteps.value.find((step) => step.id === props.log.step_id)
);

const blockName = computed(() => {
  return step.value?.title || "-";
});

const blockOutcomeColor = computed(() => {
  const rgb = props.log.outcome
    ? COLORS[STATUS_COLOR_MAP[props.log.outcome]]
    : COLORS[15];

  return `rgb(${rgb})`;
});

const validateCondition = (
  outcome: IDataOrchestrationFlowOutcome,
  condition: IDataOrchestrationFlowCheckCondition
) => {
  const context = props.log.context?.log?.find(
    (logContext) =>
      outcome.outcome === logContext.outcome &&
      logContext.attribute === condition.attribute
  );
  if (props.activeTab === t("ORCHESTRATION.CONDITION_MET")) {
    return context?.attribute_result === true;
  }
  if (props.activeTab === t("ORCHESTRATION.CONDITION_NOT_MET")) {
    return context?.attribute_result === false;
  }
  return true;
};

const templateStepOutcomes = computed(() => {
  const data = step.value?.outcomes
    .map((outcome) => ({
      ...outcome,
      checks: compact(
        outcome.checks?.map((check) => {
          const checkConditions = check.conditions.filter((cond) =>
            validateCondition(outcome, cond)
          );
          if (!checkConditions.length) {
            return false;
          }
          return {
            ...check,
            conditions: checkConditions,
          };
        })
      ),
    }))
    .filter((outcome) => Boolean(outcome.checks.length));

  return compact(data);
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.line-separator {
  @apply m-1 grow border-b border-gray-200;
}

.block-outcome {
  color: v-bind(blockOutcomeColor);
}
</style>
